import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Shield, Activity, TrendingUp, Upload, Search, Home, Users, Scroll, Settings } from 'lucide-react';
import { useDocuments } from '../hooks/useDocuments';
import { useResponsive } from '../hooks/useResponsive';
import { useSentryBreadcrumb } from '../lib/sentry';
import ErrorBoundary, { DocumentErrorFallback } from '../components/molecules/ErrorBoundary';
import { DashboardStatsLoading, DocumentGridLoading } from '../components/molecules/LoadingStates';
import ResponsiveContainer, { ResponsiveGrid, ResponsiveStack } from '../components/molecules/ResponsiveContainer';
import Sidebar from '../components/organisms/Sidebar';
import DocumentGrid from '../components/organisms/DocumentGrid';
import SearchBar from '../components/molecules/SearchBar';
import SecureUploadModal from '../components/organisms/SecureUploadModal';
import SharedWithMePage from '../components/organisms/SharedWithMePage';
import WillWitnessPage from '../components/organisms/WillWitnessPage';
import ActivityLogPage from '../components/organisms/ActivityLogPage';
import SettingsPage from '../components/organisms/SettingsPage';
import Card from '../components/atoms/Card';
import Button from '../components/atoms/Button';

const DashboardPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const { isMobile, isTablet } = useResponsive();
  
  const { documents, isLoading, error } = useDocuments();

  // Track user navigation for debugging
  useSentryBreadcrumb('navigation', `Dashboard section: ${activeSection}`);

  const filteredDocuments = documents.filter(doc =>
    doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const stats = {
    totalDocuments: documents.length,
    encryptedDocuments: documents.filter(d => d.isEncrypted || d.is_encrypted).length,
    notarizedDocuments: documents.filter(d => d.isNotarized || d.is_notarized).length,
    sharedDocuments: documents.filter(d => d.accessLevel === 'shared' || d.access_level === 'shared').length,
  };

  const renderDashboardContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return (
          <ResponsiveContainer maxWidth="full" padding="none">
            <div className="space-y-6 lg:space-y-8">
              {/* Stats Overview */}
              <div>
                <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4 lg:mb-6">
                  Dashboard Overview
                </h2>
                
                {isLoading ? (
                  <DashboardStatsLoading />
                ) : (
                  <ResponsiveGrid 
                    columns={{ sm: 1, md: 2, lg: 4 }}
                    gap={isMobile ? 'sm' : 'md'}
                  >
                    <StatCard
                      title="Total Documents"
                      value={stats.totalDocuments}
                      icon={FileText}
                      color="primary"
                    />
                    <StatCard
                      title="Encrypted"
                      value={stats.encryptedDocuments}
                      icon={Shield}
                      color="green"
                    />
                    <StatCard
                      title="Notarized"
                      value={stats.notarizedDocuments}
                      icon={Activity}
                      color="blue"
                    />
                    <StatCard
                      title="Shared"
                      value={stats.sharedDocuments}
                      icon={TrendingUp}
                      color="purple"
                    />
                  </ResponsiveGrid>
                )}
              </div>

              {/* Quick Actions */}
              <Card className="p-4 lg:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <ResponsiveStack 
                  direction={isMobile ? 'vertical' : 'horizontal'} 
                  spacing="sm"
                >
                  <Button
                    variant="primary"
                    icon={Upload}
                    onClick={() => setShowUpload(true)}
                    className={isMobile ? 'w-full' : ''}
                  >
                    Secure Upload
                  </Button>
                  <Button
                    variant="outline"
                    icon={Search}
                    onClick={() => setActiveSection('documents')}
                    className={isMobile ? 'w-full' : ''}
                  >
                    Browse Documents
                  </Button>
                  <Button
                    variant="outline"
                    icon={Scroll}
                    onClick={() => setActiveSection('will-witness')}
                    className={isMobile ? 'w-full' : ''}
                  >
                    Will & Witness
                  </Button>
                </ResponsiveStack>
              </Card>

              {/* Recent Documents */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Documents</h3>
                <ErrorBoundary fallback={DocumentErrorFallback}>
                  {isLoading ? (
                    <DocumentGridLoading count={isMobile ? 2 : isTablet ? 4 : 8} />
                  ) : (
                    <DocumentGrid documents={documents.slice(0, isMobile ? 2 : isTablet ? 4 : 8)} />
                  )}
                </ErrorBoundary>
              </div>
            </div>
          </ResponsiveContainer>
        );

      case 'documents':
        return (
          <ResponsiveContainer maxWidth="full" padding="none">
            <div className="space-y-6">
              <ResponsiveStack 
                direction={isMobile ? 'vertical' : 'horizontal'}
                justify="between"
                align={isMobile ? 'stretch' : 'center'}
                spacing="sm"
              >
                <h2 className="text-xl lg:text-2xl font-bold text-gray-900">My Documents</h2>
                <Button
                  variant="primary"
                  icon={Upload}
                  onClick={() => setShowUpload(true)}
                  size={isMobile ? 'md' : 'md'}
                  className={isMobile ? 'w-full' : ''}
                >
                  Secure Upload
                </Button>
              </ResponsiveStack>

              <SearchBar
                onSearch={setSearchQuery}
                onFilter={(filters) => console.log('Filters:', filters)}
              />

              <ErrorBoundary fallback={DocumentErrorFallback}>
                {isLoading ? (
                  <DocumentGridLoading count={isMobile ? 4 : isTablet ? 6 : 12} />
                ) : (
                  <DocumentGrid documents={filteredDocuments} />
                )}
              </ErrorBoundary>
            </div>
          </ResponsiveContainer>
        );

      case 'vault':
        return (
          <ResponsiveContainer maxWidth="full" padding="none">
            <div className="space-y-6">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Private Vault</h2>
              <Card className="p-6 lg:p-8 text-center">
                <Shield className="h-12 lg:h-16 w-12 lg:w-16 text-primary-500 mx-auto mb-4" />
                <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mb-2">
                  Maximum Security Zone
                </h3>
                <p className="text-gray-600 text-sm lg:text-base">
                  Your most sensitive documents with enhanced encryption and access controls.
                </p>
              </Card>
              
              <ErrorBoundary fallback={DocumentErrorFallback}>
                {isLoading ? (
                  <DocumentGridLoading count={isMobile ? 4 : isTablet ? 6 : 8} />
                ) : (
                  <DocumentGrid 
                    documents={documents.filter(d => 
                      (d.accessLevel === 'private' || d.access_level === 'private')
                    )} 
                  />
                )}
              </ErrorBoundary>
            </div>
          </ResponsiveContainer>
        );

      case 'shared':
        return (
          <ErrorBoundary fallback={DocumentErrorFallback}>
            <SharedWithMePage />
          </ErrorBoundary>
        );

      case 'will-witness':
        return (
          <ErrorBoundary fallback={DocumentErrorFallback}>
            <WillWitnessPage />
          </ErrorBoundary>
        );

      case 'activity':
        return (
          <ErrorBoundary fallback={DocumentErrorFallback}>
            <ActivityLogPage />
          </ErrorBoundary>
        );

      case 'settings':
        return (
          <ErrorBoundary fallback={DocumentErrorFallback}>
            <SettingsPage />
          </ErrorBoundary>
        );

      default:
        return (
          <ResponsiveContainer maxWidth="full" padding="none">
            <Card className="p-6 lg:p-8 text-center">
              <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mb-2">
                {activeSection.charAt(0).toUpperCase() + activeSection.slice(1)}
              </h3>
              <p className="text-gray-600">This section is under development.</p>
            </Card>
          </ResponsiveContainer>
        );
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <DocumentErrorFallback 
          error={error} 
          resetError={() => window.location.reload()} 
        />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar - Hidden on mobile, shown as overlay when needed */}
      <div className={`
        ${isMobile ? 'hidden' : 'block'}
        flex-shrink-0
      `}>
        <Sidebar 
          activeSection={activeSection} 
          onSectionChange={setActiveSection} 
          onUploadClick={() => setShowUpload(true)}
        />
      </div>
      
      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className={`p-4 sm:p-6 lg:p-8 max-w-full ${isMobile ? 'pt-16 pb-20' : ''}`}>
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full"
          >
            {renderDashboardContent()}
          </motion.div>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-bottom z-50">
          <div className="grid grid-cols-5 gap-1 p-2">
            <Button
              variant={activeSection === 'dashboard' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setActiveSection('dashboard')}
              className="flex flex-col items-center gap-1 h-auto py-2 px-1"
            >
              <Home size={16} />
              <span className="text-xs">Home</span>
            </Button>
            <Button
              variant={activeSection === 'documents' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setActiveSection('documents')}
              className="flex flex-col items-center gap-1 h-auto py-2 px-1"
            >
              <FileText size={16} />
              <span className="text-xs">Files</span>
            </Button>
            <Button
              variant={activeSection === 'shared' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setActiveSection('shared')}
              className="flex flex-col items-center gap-1 h-auto py-2 px-1"
            >
              <Users size={16} />
              <span className="text-xs">Shared</span>
            </Button>
            <Button
              variant={activeSection === 'activity' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setActiveSection('activity')}
              className="flex flex-col items-center gap-1 h-auto py-2 px-1"
            >
              <Activity size={16} />
              <span className="text-xs">Activity</span>
            </Button>
            <Button
              variant={activeSection === 'settings' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setActiveSection('settings')}
              className="flex flex-col items-center gap-1 h-auto py-2 px-1"
            >
              <Settings size={16} />
              <span className="text-xs">Settings</span>
            </Button>
          </div>
        </div>
      )}

      {/* Secure Upload Modal */}
      <SecureUploadModal 
        isOpen={showUpload} 
        onClose={() => setShowUpload(false)} 
      />
    </div>
  );
};

// Stat Card Component
interface StatCardProps {
  title: string;
  value: number;
  icon: React.ComponentType<any>;
  color: 'primary' | 'green' | 'blue' | 'purple';
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    primary: 'bg-primary-100 text-primary-600',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <Card className="p-4 lg:p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl lg:text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="h-5 w-5 lg:h-6 lg:w-6" />
        </div>
      </div>
    </Card>
  );
};

export default DashboardPage;